import { useState, useMemo } from 'react'
import { Eye, EyeOff, Link, ChevronUp, ChevronDown, Radio, Calendar } from 'lucide-react'
import { liveStatusLabel, liveStatusClass } from '../../../utils/liveStatus'

export default function CatalogTable({
  items,
  loading,
  selected,
  checked,
  onSelect,
  onToggle,
  onToggleAll,
  onToggleSchedule
}) {
  const [sortKey, setSortKey] = useState(null)
  const [sortDir, setSortDir] = useState('asc')

  const allChecked = items.length > 0 && items.every(item => checked.has(item.tbtv_id))

  function handleSort(key) {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc')
      else { setSortKey(null); setSortDir('asc') }
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sortedItems = useMemo(() => {
    if (!sortKey) return items
    const sorted = [...items].sort((a, b) => {
      let aVal, bVal
      switch (sortKey) {
        case 'title':
          aVal = (a.title || '').toLowerCase()
          bVal = (b.title || '').toLowerCase()
          return aVal.localeCompare(bVal)
        case 'status':
          aVal = a.status === 'active' ? 0 : 1
          bVal = b.status === 'active' ? 0 : 1
          return aVal - bVal
        case 'type':
          aVal = (a.video_type || '').toLowerCase()
          bVal = (b.video_type || '').toLowerCase()
          return aVal.localeCompare(bVal)
        case 'quality':
          const qOrder = { 'UHD': 0, 'FHD': 1, 'HD': 2, 'SD': 3 }
          aVal = qOrder[a.quality] ?? 99
          bVal = qOrder[b.quality] ?? 99
          return aVal - bVal
        case 'duration':
          aVal = a.duration || 0
          bVal = b.duration || 0
          return aVal - bVal
        case 'date':
          aVal = a.release_date ? new Date(a.release_date).getTime() : 0
          bVal = b.release_date ? new Date(b.release_date).getTime() : 0
          return aVal - bVal
        case 'tags':
          aVal = (a.tags || []).length
          bVal = (b.tags || []).length
          return aVal - bVal
        default:
          return 0
      }
    })
    return sortDir === 'desc' ? sorted.reverse() : sorted
  }, [items, sortKey, sortDir])

  function SortHeader({ label, sortId, className }) {
    const active = sortKey === sortId
    return (
      <th className={`${className || ''} th-sortable`} onClick={() => handleSort(sortId)}>
        <span className="th-sort-inner">
          {label}
          {active ? (
            sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
          ) : (
            <span className="th-sort-idle">⇅</span>
          )}
        </span>
      </th>
    )
  }

  function formatDuration(secs) {
    if (!secs || secs <= 0) return '—'
    const h = Math.floor(secs / 3600)
    const m = Math.floor((secs % 3600) / 60)
    const s = secs % 60
    if (h > 0) return `${h}h ${m}m`
    if (m > 0) return `${m}m ${s}s`
    return `${s}s`
  }

  function formatDate(dateStr) {
    if (!dateStr) return '—'
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  if (loading && items.length === 0) {
    return (
      <div className="vod-table-wrap">
        <table className="vod-table">
          <thead><tr><th colSpan="9" style={{ textAlign: 'center', padding: 20, color: '#999' }}>Loading...</th></tr></thead>
        </table>
      </div>
    )
  }

  return (
    <div className="vod-table-wrap">
      <table className="vod-table">
        <thead>
          <tr>
            <th className="th-check">
              <input
                type="checkbox"
                checked={allChecked}
                onChange={() => onToggleAll(items)}
              />
            </th>
            <th className="th-thumb">Thumb</th>
            <SortHeader label="Title" sortId="title" className="th-title" />
            <SortHeader label="Status" sortId="status" className="th-status" />
            <SortHeader label="Type" sortId="type" className="th-type" />
            <SortHeader label="Quality" sortId="quality" className="th-quality" />
            <SortHeader label="Duration" sortId="duration" className="th-duration" />
            <SortHeader label="Released" sortId="date" className="th-date" />
            <SortHeader label="Tags" sortId="tags" className="th-tags" />
          </tr>
        </thead>
        <tbody>
          {sortedItems.length === 0 ? (
            <tr><td colSpan="9" className="vod-table-empty">No items found. Try adjusting filters or add new content.</td></tr>
          ) : (
            sortedItems.map(item => (
              <tr
                key={item.tbtv_id}
                className={`${selected?.tbtv_id === item.tbtv_id ? 'active' : ''} ${checked.has(item.tbtv_id) ? 'checked' : ''}`}
                onClick={() => onSelect(item)}
              >
                <td className="td-check" onClick={e => { e.stopPropagation(); onToggle(item.tbtv_id) }}>
                  <input
                    type="checkbox"
                    checked={checked.has(item.tbtv_id)}
                    onChange={() => {}}
                  />
                </td>
                <td className="td-thumb">
                  {item.thumbnail_url ? (
                    <img src={item.thumbnail_url} alt="" />
                  ) : (
                    <div className="vod-thumb-placeholder" />
                  )}
                </td>
                <td className="td-title" title={item.title}>
                  {item.title || 'Untitled'}
                </td>
                <td className="td-status">
                  <span className={`vod-status-badge ${item.status}`}>
                    {item.status === 'active' ? <Eye size={10} /> : <EyeOff size={10} />}
                    {item.status}
                  </span>
                  {item.event_mode === 'live' && (
                    <>
                      <span className={`event-mode-badge live ${liveStatusClass(item.live_v2_status, item.event_type)}`} style={{ marginLeft: 4 }}>
                        {liveStatusLabel(item.live_v2_status, item.event_type)}
                      </span>
                      <button
                        className={`schedule-toggle-btn ${item.include_in_schedule ? 'scheduled' : ''}`}
                        onClick={e => { e.stopPropagation(); onToggleSchedule && onToggleSchedule(item) }}
                        title={item.include_in_schedule ? 'In live schedule — click to remove' : 'Not in live schedule — click to add'}
                      >
                        <Calendar size={10} />
                        {item.include_in_schedule ? 'Sched' : 'Unsched'}
                      </button>
                      {item.youtube_simulcast_mode === 'simulcast' && (
                        <span className="yt-simulcast-badge" style={{ marginLeft: 4 }} title={`Simulcasting to YouTube${item.youtube_privacy ? ` (${item.youtube_privacy})` : ''}`}>
                          <Radio size={10} /> YT
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td className="td-type">
                  <span className="vod-type-badge">{item.video_type || 'HLS'}</span>
                </td>
                <td className="td-quality">
                  <span className="vod-quality-badge">{item.quality || 'FHD'}</span>
                </td>
                <td className="td-duration">
                  {item.event_mode === 'live'
                    ? `${item.duration_minutes || 120}m`
                    : formatDuration(item.duration)}
                </td>
                <td className="td-date">
                  {item.event_mode === 'live' && item.scheduled_start
                    ? new Date(item.scheduled_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : formatDate(item.release_date)}
                </td>
                <td className="td-tags">
                  {item.tags && item.tags.length > 0 ? (
                    <div className="vod-table-tags">
                      {item.tags.slice(0, 2).map(t => (
                        <span key={t} className="tag-chip-mini">{t}</span>
                      ))}
                      {item.tags.length > 2 && (
                        <span className="tag-chip-mini more">+{item.tags.length - 2}</span>
                      )}
                    </div>
                  ) : '—'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
